import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  PRICING_TIERS,
  getTierById,
  getTierForCompany,
  getNextTier,
  effectiveUsageTier,
  type PricingTier,
  type Role,
} from "@/lib/pricing-tiers";
import {
  applyTrialCreditCap,
  resolveTrialPosture,
  type TrialCompanyRow,
  type TrialPosture,
} from "@/lib/trial";
import { creditResetNote, daysUntilCreditsReset } from "@/lib/credit-period";

/**
 * Single source of truth for the dashboard header plan + usage strip.
 *
 * The headline metric is unified CREDITS (credits_v2 has been live since
 * 2026-06-22): every metered action — carrier/broker lookups AND packet scans —
 * draws from one monthly `credits` balance, and a scan costs more than a lookup
 * (page-banded x scan-quality). The strip reads:
 *   companies.subscription_tier, logistics_role, is_test_tenant, settings.payment_status,
 *     credits_period_start
 *   usage_counters.credits_used  (period resolved via usage_credits_period_key so a
 *     billing-cycle-anchored subscriber's YYYY-MM-DD row is not missed)
 *   usage_limits.monthly_credits (the enforced cap, keyed by the backend-effective tier)
 *
 * Realtime: subscribes to postgres_changes on usage_counters and companies so the
 * strip updates the moment a Stripe/Paddle webhook lands or a credit is spent.
 */

const TEST_TENANT_LIMIT_OVERRIDE: number | null = null;

type CompanyRow = {
  id: string;
  subscription_tier: string | null;
  logistics_role: string | null;
  billing_status: string | null;
  paddle_subscription_id: string | null;
  // Read alongside paddle so resolveTrialPosture() sees the same signals the
  // server does — a legacy Stripe subscriber and a comped tenant must not be
  // mistaken for a trial and handed the 100-credit ceiling.
  stripe_subscription_id: string | null;
  billing_exempt: boolean | null;
  is_test_tenant: boolean | null;
  trial_started_at: string | null;
  trial_ends_at: string | null;
  settings: Record<string, unknown> | null;
  // Billing-cycle anchor (Paddle current_billing_period.starts_at). When set, the
  // credit meter resets monthly on this day-of-month instead of the calendar 1st.
  credits_period_start: string | null;
};


export type PlanUsageStatus =
  | "loading"
  | "hidden"
  | "free"
  | "trial"
  | "active"
  | "past_due"
  | "canceled"
  | "test_tenant"
  | "no_plan";

export interface PlanUsageState {
  status: PlanUsageStatus;
  tier: PricingTier | null;
  nextTier: PricingTier | null;
  role: Role | null;
  used: number;
  limit: number;
  unlimited: boolean;
  percent: number;
  metricLabel: string;
  resetDays: number;
  /** Trial classification (mirrored local copy of the server's). */
  posture: TrialPosture;
  /** Trial-aware one-clause note: a trial's allowance ENDS, it never refills,
   *  so "Resets in 21d" must not be shown over one. */
  resetNote: string;
  trialDaysLeft: number | null;
  isAdmin: boolean;
  isTestTenant: boolean;
  /** Prepaid credit-pack balance (non-expiring top-up credits). 0 when the
   *  credit-packs feature is unused. Spent AFTER the monthly allowance. */
  packBalance: number;
  /** True when a refund/chargeback consumed already-spent credits and the
   *  account is blocked from buying more packs until an admin clears it. */
  purchasesBlocked: boolean;
}

function currentPeriod(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.ceil((t - Date.now()) / 86400000));
}

function resolveRole(logisticsRole: string | null | undefined): Role {
  const r = (logisticsRole ?? "").toLowerCase();
  if (r === "broker") return "broker";
  if (r === "carrier" || r === "trucker") return "carrier";
  return "dispatcher";
}

/**
 * Resolve a PricingTier from the canonical companies.subscription_tier value,
 * with role-aware fallbacks (a broker on no plan -> broker-free, dispatcher
 * with no plan but stripe_subscription_id treated as Solo, etc).
 */
function resolveTier(
  subscriptionTier: string | null | undefined,
  role: Role,
): PricingTier | null {
  if (subscriptionTier) {
    // getTierForCompany resolves role-scoped ids and the "enterprise" cross-
    // role collision (broker's literal "enterprise" vs dispatcher's
    // "dispatcher-enterprise"), but its final fallback is role-blind — so
    // still gate on `.role === role` here. The bare "free"/"pro" ids are
    // shared across roles, so a role-blind match made a free dispatcher or
    // broker inherit the CARRIER Free tier's unlimited (-1) cap — the false
    // "unlimited lookups" on the dashboard.
    const direct = getTierForCompany(subscriptionTier, role);
    if (direct && direct.role === role) return direct;
  }
  // Role-aware free/entry fallback when there's no role-matching plan.
  if (role === "broker") return getTierById("broker-free") ?? null;
  if (role === "carrier") return getTierById("free") ?? null;
  if (role === "dispatcher") return getTierById("dispatcher-trial") ?? null;
  return null;
}

// The headline metric is now unified credits for every role, so there is no
// per-role column/label split. `credits_used` is read directly (see below).
const METRIC_LABEL = "credits";

export function usePlanUsage(companyId: string | null): PlanUsageState {
  const qc = useQueryClient();

  const { data: company, isLoading: companyLoading } = useQuery({
    queryKey: ["plan-usage", "company", companyId],
    queryFn: async (): Promise<CompanyRow | null> => {
      if (!companyId) return null;
      const { data } = await supabase
        .from("companies")
        .select(
          "id, subscription_tier, logistics_role, billing_status, paddle_subscription_id, " +
            "stripe_subscription_id, billing_exempt, is_test_tenant, settings, " +
            "credits_period_start, trial_started_at, trial_ends_at",
        )
        .eq("id", companyId)
        .maybeSingle();
      if (!data) return null;
      return data as unknown as CompanyRow;
    },
    enabled: !!companyId,
    staleTime: 30_000,
  });

  // Backend-effective usage_limits tier (mirrors check_usage_limit) — the key the
  // monthly_credits cap is stored under. Null until the company row loads.
  const effTier = company
    ? effectiveUsageTier(company.subscription_tier, company.logistics_role)
    : null;

  // Unified credits consumed this period. Resolve the correct period key FIRST:
  // billing-cycle-anchored subscribers key their row YYYY-MM-DD, calendar tenants
  // YYYY-MM, so a fixed calendar-period read would miss a subscriber's real row.
  const { data: creditsUsed } = useQuery({
    queryKey: ["plan-usage", "credits-used", companyId],
    queryFn: async (): Promise<number> => {
      if (!companyId) return 0;
      const { data: pk } = await supabase.rpc("usage_credits_period_key", {
        p_company_id: companyId,
        p_metric: "credits",
      });
      const periodKey = typeof pk === "string" && pk ? pk : currentPeriod();
      const { data } = await supabase
        .from("usage_counters")
        .select("credits_used")
        .eq("company_id", companyId)
        .eq("period", periodKey)
        .maybeSingle();
      return Math.max(0, Number((data as { credits_used?: number | null } | null)?.credits_used ?? 0));
    },
    enabled: !!companyId,
    staleTime: 15_000,
  });

  // Monthly credit cap from the DB (source of truth; the frontend tier def's
  // `verifications` value can drift from usage_limits, e.g. free/verified-plus).
  // A null monthly_credits means no cap (-1 = unlimited).
  const { data: creditLimit } = useQuery({
    queryKey: ["plan-usage", "credit-limit", effTier],
    queryFn: async (): Promise<number> => {
      if (!effTier) return -1;
      const { data } = await supabase
        .from("usage_limits")
        .select("monthly_credits")
        .eq("tier", effTier)
        .maybeSingle();
      const v = (data as { monthly_credits?: number | null } | null)?.monthly_credits;
      return typeof v === "number" ? v : -1;
    },
    enabled: !!effTier,
    staleTime: 5 * 60_000,
  });

  // Prepaid credit-pack balance (non-expiring top-up credits). RLS lets a member
  // read their own row; absent row = 0 balance. Realtime-invalidated below.
  const { data: packRow } = useQuery({
    queryKey: ["plan-usage", "pack-balance", companyId],
    queryFn: async (): Promise<{ balance: number; purchasesBlocked: boolean }> => {
      if (!companyId) return { balance: 0, purchasesBlocked: false };
      const { data } = await supabase
        .from("company_credit_balance")
        .select("balance, purchases_blocked")
        .eq("company_id", companyId)
        .maybeSingle();
      const row = data as { balance?: number | null; purchases_blocked?: boolean | null } | null;
      return {
        balance: Math.max(0, Number(row?.balance ?? 0)),
        purchasesBlocked: row?.purchases_blocked === true,
      };
    },
    enabled: !!companyId,
    staleTime: 15_000,
  });

  const { data: isAdmin = false } = useQuery({
    queryKey: ["plan-usage", "is-admin", companyId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("current_user_has_role", {
        _role: "admin",
      });
      if (error) return false;
      return !!data;
    },
    enabled: !!companyId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  // Realtime: invalidate on usage_counters or companies change for this tenant.
  useEffect(() => {
    if (!companyId) return;
    // Mint a fresh id INSIDE the effect so every mount gets a unique channel name.
    // usePlanUsage mounts twice at once on /dash (TopBar PlanUsageStrip + home UsageModule)
    // with the same companyId; a stable topic makes supabase.channel() return the
    // already-subscribed channel and the second .on() throws
    // "cannot add postgres_changes callbacks after subscribe()", crashing the page.
    // Same fix as useFeatureOverrideRealtime in usePhaseFeature.ts.
    const uniqueId = (typeof crypto !== "undefined" && "randomUUID" in crypto)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const channel = supabase
      .channel(`plan-usage-${companyId}:${uniqueId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "usage_counters",
          filter: `company_id=eq.${companyId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: ["plan-usage", "credits-used", companyId] });
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "companies",
          filter: `id=eq.${companyId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: ["plan-usage", "company", companyId] });
        },
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "company_credit_balance",
          filter: `company_id=eq.${companyId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: ["plan-usage", "pack-balance", companyId] });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [companyId, qc]);

  return useMemo<PlanUsageState>(() => {
    if (!companyId || companyLoading) {
      return blank("loading", isAdmin);
    }
    if (!company) {
      return blank("hidden", isAdmin);
    }

    const resetDays = daysUntilCreditsReset(company.credits_period_start);
    const role = resolveRole(company.logistics_role);
    const tier = resolveTier(company.subscription_tier, role);
    const used = Math.max(0, Number(creditsUsed ?? 0));
    // DB monthly_credits is authoritative; fall back to the tier def's credits
    // value (limits.verifications) only while the limit query is in flight.
    const planLimit = typeof creditLimit === "number" ? creditLimit : (tier?.limits.verifications ?? -1);
    // The 14-day trial caps EVERY plan at 100 credits, so the meter must show
    // the ceiling, not the plan's own allowance — a Broker Scale trial showing
    // "0 / 7,500" would promise 7,500 free credits the server will not grant.
    // check_usage_limit applies the identical ceiling; this only renders it.
    // resolveTrialPosture is the mirrored classifier, run over the same company
    // row, so the meter and the enforcement agree without a second round trip.
    //
    // The effective BUCKET is passed too, and it matters: an account on the
    // auto-started trial has not picked a plan yet, so it resolves to
    // dispatcher-trial / broker-free, whose stored allowance is the post-trial
    // paywall's 0. Without the bucket, min(0, 100) renders "0 / 0 credits" at
    // the very moment the server is granting 100.
    const posture = resolveTrialPosture(company as unknown as TrialCompanyRow);
    const rawLimit = applyTrialCreditCap(planLimit, posture, effTier);
    const unlimited = rawLimit === -1;
    const limit = unlimited ? Infinity : rawLimit;
    const percent = unlimited || limit <= 0 ? 0 : Math.min(100, Math.round((used / limit) * 100));

    const billingStatus = (company.billing_status ?? "").toLowerCase();
    const paymentStatus = (
      (company.settings as { payment_status?: string } | null)?.payment_status ?? ""
    ).toLowerCase();
    const trialDaysLeft = daysUntil(company.trial_ends_at);
    const isTestTenant = !!company.is_test_tenant;
    const hasPaddleSub = !!company.paddle_subscription_id;

    // Status priority: admin > test_tenant > past_due > trial > active > free > no_plan
    // Uses canonical billing_status (set by paddle-webhook) instead of Stripe-specific signals.
    let status: PlanUsageStatus = "no_plan";
    if (isAdmin) {
      status = "hidden";
    } else if (isTestTenant) {
      status = "test_tenant";
    } else if (paymentStatus === "past_due" || billingStatus === "past_due") {
      status = "past_due";
    } else if (billingStatus === "trial" && !hasPaddleSub) {
      status = "trial";
    } else if (billingStatus === "active" || billingStatus === "trialing" || hasPaddleSub) {
      status = "active";
    } else if (tier && (tier.id === "free" || tier.id === "broker-free")) {
      status = "free";
    } else if (tier && tier.monthly === 0) {
      status = "free";
    } else if (!tier) {
      status = "no_plan";
    } else {
      status = "active";
    }

    return {
      status,
      posture,
      resetNote: creditResetNote({ posture, trialDaysLeft, resetDays }),
      tier,
      nextTier: tier ? getNextTier(tier.id) ?? null : null,
      role,
      used,
      limit: unlimited ? -1 : limit,
      unlimited,
      percent,
      metricLabel: METRIC_LABEL,
      resetDays,
      trialDaysLeft,
      isAdmin,
      isTestTenant,
      packBalance: Math.max(0, Number(packRow?.balance ?? 0)),
      purchasesBlocked: packRow?.purchasesBlocked === true,
    };
  }, [companyId, companyLoading, company, creditsUsed, creditLimit, effTier, isAdmin, packRow]);
}

function blank(status: PlanUsageStatus, isAdmin: boolean): PlanUsageState {
  return {
    status,
    tier: null,
    nextTier: null,
    role: null,
    used: 0,
    limit: 0,
    unlimited: false,
    percent: 0,
    metricLabel: "credits",
    resetDays: 0,
    posture: "unknown",
    resetNote: "",
    trialDaysLeft: null,
    isAdmin,
    isTestTenant: false,
    packBalance: 0,
    purchasesBlocked: false,
  };
}

// Re-export for downstream tooling.
export { PRICING_TIERS };
